import React, { useState, useEffect } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import {
  SignupAction,
  PreviewCompanyInviteAction,
} from "../../store/action";
import { UserRole } from "../../constants/roles";
import { TERMS_VERSION } from "../../constants/legal";
import { termsContent, privacyContent } from "../legal/legalContent";
import LegalModal from "../legal/LegalModal";
import AuthShell from "../ui/AuthShell";
import GoogleSignIn from "../googleSignIn/GoogleSignIn";
import { usePageMeta } from "../../hooks/usePageMeta";
import { trackEvent } from "../../utils/metaPixel";
import Toast from "../ui/Toast";
import {
  fieldClass,
  labelClass,
  passwordFieldClass,
  passwordFieldErrorClass,
  primaryButtonClass,
  textLinkClass,
} from "../ui/formStyles";

/**
 * The API's PasswordPolicy.MinimumLength. Checked here as well so the refusal
 * arrives before the round trip, under the field and next to the hint — not as
 * a `minLength` bubble on top of it, which would say the same thing twice.
 */
const MIN_PASSWORD_LENGTH = 8;

/**
 * Signup page: one form, with the account type as a toggle at its top.
 *
 * e2e contract (signup.spec.js): `.signup-content` wraps everything; the
 * account type is a radio group named by "Account type"; the fields are found
 * by their labels ("First Name", "Last Name", "Email", "Password"), all
 * `required`. Those strings live in the `en` bundle under `Signup.*` — the spec
 * drives a real browser, which resolves to `en`, so rewording one there breaks it.
 */
const Signup = ({ authenticated }) => {
  const { t } = useTranslation();
  const location = useLocation();
  usePageMeta({ title: `${t("Signup.title")} | EasyPlanner` });
  const inviteToken = new URLSearchParams(location.search).get("invite");

  // null while the token is still being checked, so the banner does not flash
  // "invalid" for a perfectly good link on a slow connection.
  const [invite, setInvite] = useState(null);

  // Business first: it is who the site is selling to, and who arrives from a
  // pricing card. Someone who only books flips it with one tap.
  const [accountType, setAccountType] = useState(UserRole.COMPANY_ADMIN);
  const [registeredEmail, setRegisteredEmail] = useState("");

  useEffect(() => {
    if (!inviteToken) return;

    let cancelled = false;
    PreviewCompanyInviteAction(inviteToken)
      .then((result) => {
        if (!cancelled) setInvite(result);
      })
      .catch(() => {
        // The lookup itself failing is not the invitee's problem — let them
        // register normally rather than stranding them on an error.
        if (!cancelled) setInvite({ isValid: false, reason: "notFound" });
      });

    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  if (authenticated) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  // An invitation is for a business, so it fixes the account type and the
  // toggle goes away. Only once the token checks out — a stale link must not
  // silently steer anyone into the wrong account type.
  const inviteValid = Boolean(invite?.isValid);
  const role = inviteValid ? UserRole.COMPANY_ADMIN : accountType;

  return (
    <AuthShell className="signup-content w-full max-w-lg">
      {registeredEmail ? (
        <CheckYourEmail email={registeredEmail} />
      ) : (
        <>
          {inviteToken && invite && <InviteBanner invite={invite} />}

          <div className="text-center">
            <h1 className="signup-title text-fluid-xl font-bold tracking-tight text-ink-900">
              {t("Signup.title")}
            </h1>
            <p className="mt-2 text-fluid-sm text-ink-500">
              {role === UserRole.COMPANY_ADMIN
                ? t("Signup.accountType.businessBlurb")
                : t("Signup.accountType.personalBlurb")}
            </p>
          </div>

          <SignupForm
            role={role}
            onRoleChange={inviteValid ? null : setAccountType}
            inviteToken={inviteValid ? inviteToken : null}
            onRegistered={setRegisteredEmail}
          />
        </>
      )}

      <span className="login-link mt-6 block text-center text-fluid-sm text-ink-500">
        {t("Signup.haveAccount")}{" "}
        <Link to="/login" className={textLinkClass}>
          {t("Signup.loginLink")}
        </Link>
      </span>
    </AuthShell>
  );
};

Signup.propTypes = {
  authenticated: PropTypes.bool,
};

// `key` names the copy under Signup.accountType.*; the wording itself lives in
// the bundles so this array stays language-free.
const ACCOUNT_TYPES = [
  { role: UserRole.COMPANY_ADMIN, icon: "bi-briefcase", key: "business" },
  { role: UserRole.USER, icon: "bi-person", key: "personal" },
];

/**
 * Native radios, visually hidden, under labels styled as two buttons: arrow
 * keys move between them and a screen reader hears "radio, 1 of 2" without
 * any ARIA of ours.
 */
const AccountTypePicker = ({ value, onChange }) => {
  const { t } = useTranslation();

  return (
    <fieldset className="account-type">
      <legend className={labelClass}>{t("Signup.accountType.label")}</legend>
      <div className="grid grid-cols-2 gap-3">
        {ACCOUNT_TYPES.map(({ role, icon, key }) => {
          const selected = value === role;
          return (
            <label
              key={role}
              className={`flex min-h-12 min-w-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border px-2 py-2.5 text-center text-fluid-sm font-medium transition has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-brand-600 sm:flex-row sm:gap-2 ${
                selected
                  ? "border-brand-600 bg-brand-50 text-brand-700"
                  : "border-ink-200 bg-white text-ink-600 hover:border-ink-300 hover:text-ink-800"
              }`}
            >
              <input
                type="radio"
                name="accountType"
                value={role}
                checked={selected}
                onChange={() => onChange(role)}
                className="sr-only"
              />
              <i className={`bi ${icon} text-base`} aria-hidden="true" />
              <span>{t(`Signup.accountType.${key}`)}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
};

AccountTypePicker.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

/**
 * What an invitation link says for itself, valid or not.
 *
 * A dead link is a note, not an error: it was probably passed along weeks after
 * it was sent, and the person holding it can still register perfectly well —
 * their company just arrives unassigned. Blocking them would lose a customer to
 * protect nothing.
 */
const InviteBanner = ({ invite }) => {
  const { t } = useTranslation();

  if (invite.isValid) {
    return (
      <div className="invite-banner mb-5 flex items-start gap-2.5 rounded-lg border border-brand-200 bg-brand-50 px-4 py-3 text-fluid-sm text-brand-800">
        <i className="bi bi-envelope-check mt-0.5" aria-hidden="true" />
        <span>{t("Signup.invite.valid", { name: invite.invitedByName })}</span>
      </div>
    );
  }

  return (
    <div className="invite-banner mb-5 flex items-start gap-2.5 rounded-lg border border-ink-200 bg-ink-50 px-4 py-3 text-fluid-sm text-ink-600">
      <i className="bi bi-info-circle mt-0.5" aria-hidden="true" />
      <span>{t(`Signup.invite.${invite.reason ?? "notFound"}`)}</span>
    </div>
  );
};

InviteBanner.propTypes = {
  invite: PropTypes.shape({
    isValid: PropTypes.bool,
    reason: PropTypes.string,
    invitedByName: PropTypes.string,
  }).isRequired,
};

/**
 * `onRoleChange` is null when the account type is fixed (a valid invitation),
 * and the toggle is then not drawn at all.
 */
const SignupForm = ({ role, onRoleChange, inviteToken = null, onRegistered }) => {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // One password field with an eye instead of a second "confirm" field: the
  // user can see what they typed, which is what the confirmation was for.
  const [showPassword, setShowPassword] = useState(false);
  const [passwordLengthError, setPasswordLengthError] = useState("");
  // Unticked, always. A pre-ticked consent box is not valid agreement (CJEU
  // C-673/17, Planet49) — the act of ticking is the evidence.
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsError, setTermsError] = useState("");
  // Which legal document is open in the modal: null | "terms" | "privacy".
  // Reading a document is not the same act as consenting to it, so opening or
  // closing this never touches `acceptedTerms` — see the note in LegalModal.jsx.
  const [openLegalDoc, setOpenLegalDoc] = useState(null);
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === "firstName") setFirstName(value);
    if (name === "lastName") setLastName(value);
    if (name === "email") setEmail(value);
    if (name === "password") {
      setPassword(value);
      if (value.length >= MIN_PASSWORD_LENGTH) setPasswordLengthError("");
    }
  };

  const handleTermsChange = (event) => {
    setAcceptedTerms(event.target.checked);
    if (event.target.checked) setTermsError("");
  };

  const handleClose = () => setOpen(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      setPasswordLengthError(
        t("Signup.form.passwordTooShort", { count: MIN_PASSWORD_LENGTH }),
      );
      return;
    }

    // `required` on the input already blocks a normal submit; this catches the
    // rest — a form submitted programmatically, or a browser that treats a
    // required checkbox loosely. The API refuses it too.
    if (!acceptedTerms) {
      setTermsError(t("Signup.terms.required"));
      return;
    }

    setSubmitting(true);
    try {
      const signUpRequest = {
        firstName,
        lastName,
        email,
        password,
        role,
        acceptedTerms,
        // The version the user was actually shown. The API compares it with its
        // own and refuses a mismatch, so a page left open across a deploy that
        // changed the text cannot record agreement to wording nobody read.
        termsVersion: TERMS_VERSION,

        // Null unless they arrived through a link that checked out. The API
        // treats a bad one as absent rather than refusing the registration, but
        // there is no reason to send one we already know is dead.
        inviteToken,
      };
      await SignupAction(signUpRequest);

      // The conversion our ads are bought for: a business account, counted
      // when it is created rather than activated — activation happens on the
      // emailed /activate link, a token page the pixel never sees. Consent,
      // and an address with nothing in it, are trackEvent's to check; an
      // invited sign-up's URL holds its token, so that one is not counted.
      if (role === UserRole.COMPANY_ADMIN) {
        trackEvent("CompleteRegistration", { content_name: "business" });
      }

      // Stay put and explain the next step. Dropping the user on /login here
      // would send them straight to a form their brand-new account cannot yet
      // pass, with nothing on screen saying why.
      onRegistered(email);
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message || t("Signup.genericError"),
      );
      setOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="mt-7">
        {onRoleChange && (
          <div className="mb-5">
            <AccountTypePicker value={role} onChange={onRoleChange} />
          </div>
        )}

        {/* Right under the account type, which it carries: a newcomer through
            Google gets the type picked above, or a business account for a
            valid invitation. Its own consent line stands in for the checkbox
            below, which belongs to the email form. */}
        <GoogleSignIn
          mode="signup"
          role={role}
          inviteToken={inviteToken}
          divider="after"
        />

        <div className="form-row grid gap-4 sm:grid-cols-2">
          <div className="form-item min-w-0">
            <label htmlFor="signup-first-name" className={labelClass}>
              {t("Signup.form.firstName")}
            </label>
            <input
              type="text"
              id="signup-first-name"
              name="firstName"
              className={fieldClass}
              placeholder={t("Signup.form.firstName")}
              value={firstName}
              onChange={handleInputChange}
              autoComplete="given-name"
              required
            />
          </div>
          <div className="form-item min-w-0">
            <label htmlFor="signup-last-name" className={labelClass}>
              {t("Signup.form.lastName")}
            </label>
            <input
              type="text"
              id="signup-last-name"
              name="lastName"
              className={fieldClass}
              placeholder={t("Signup.form.lastName")}
              value={lastName}
              onChange={handleInputChange}
              autoComplete="family-name"
              required
            />
          </div>
        </div>

        <div className="form-item mt-4">
          <label htmlFor="signup-email" className={labelClass}>
            {t("Signup.form.email")}
          </label>
          <input
            type="email"
            id="signup-email"
            name="email"
            className={fieldClass}
            placeholder={t("Signup.form.emailPlaceholder")}
            value={email}
            onChange={handleInputChange}
            autoComplete="email"
            required
          />
        </div>

        <div className="form-item mt-4">
          <label htmlFor="signup-password" className={labelClass}>
            {t("Signup.form.password")}
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              id="signup-password"
              name="password"
              className={
                passwordLengthError
                  ? passwordFieldErrorClass
                  : passwordFieldClass
              }
              placeholder={t("Signup.form.passwordPlaceholder")}
              value={password}
              onChange={handleInputChange}
              autoComplete="new-password"
              aria-invalid={passwordLengthError ? "true" : undefined}
              aria-describedby="signup-password-hint"
              required
            />
            {/* One fixed name, with the state in aria-pressed: a label that
                flips between "show" and "hide" would contradict the pressed
                state a screen reader announces next to it. */}
            <button
              type="button"
              onClick={() => setShowPassword((shown) => !shown)}
              aria-label={t("Signup.form.showPassword")}
              aria-pressed={showPassword}
              aria-controls="signup-password"
              className="password-toggle absolute right-0 top-1/2 inline-flex size-11 -translate-y-1/2 items-center justify-center rounded-lg text-ink-400 transition hover:text-ink-700 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-600"
            >
              <i
                className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"} text-lg`}
                aria-hidden="true"
              />
            </button>
          </div>
          {passwordLengthError ? (
            <div
              id="signup-password-hint"
              className="error-message mt-1.5 flex items-center gap-1.5 text-xs font-medium text-red-600"
            >
              <i
                className="bi bi-exclamation-circle-fill"
                aria-hidden="true"
              />
              {passwordLengthError}
            </div>
          ) : (
            <p id="signup-password-hint" className="mt-1.5 text-xs text-ink-400">
              {t("Signup.form.passwordHint", { count: MIN_PASSWORD_LENGTH })}
            </p>
          )}
        </div>

        {/* The consent gate. The document names open a modal instead of
            navigating away — leaving the page to read a long document loses
            everything typed so far, which is how people end up ticking
            without reading. `/terms` and `/privacy` still exist as the
            addressable, linkable copy (footer, support conversations); this
            is a convenience viewer over the same content. */}
        <div className="form-item terms-consent mt-6">
          <label
            htmlFor="signup-accept-terms"
            className="flex cursor-pointer items-start gap-2.5 text-fluid-sm leading-relaxed text-ink-600"
          >
            <input
              type="checkbox"
              id="signup-accept-terms"
              name="acceptedTerms"
              className="mt-0.5 size-4 shrink-0 cursor-pointer rounded border-ink-300 text-brand-600 accent-brand-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
              checked={acceptedTerms}
              onChange={handleTermsChange}
              aria-invalid={termsError ? "true" : undefined}
              aria-describedby={termsError ? "signup-terms-error" : undefined}
              required
            />
            <span>
              {t("Signup.terms.prefix")}{" "}
              {/* stopPropagation matters here: these buttons sit inside the
                  <label>, so without it the browser's own label→control click
                  forwarding would tick the checkbox as a side effect of
                  opening the modal — turning "I clicked to read it" into a
                  false record of "I agreed to it". */}
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setOpenLegalDoc("terms");
                }}
                className={`${textLinkClass} cursor-pointer`}
              >
                {t("Signup.terms.termsLink")}
              </button>{" "}
              {t("Signup.terms.connector")}{" "}
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setOpenLegalDoc("privacy");
                }}
                className={`${textLinkClass} cursor-pointer`}
              >
                {t("Signup.terms.privacyLink")}
              </button>
              .
            </span>
          </label>

          {termsError && (
            <div
              id="signup-terms-error"
              className="error-message mt-1.5 flex items-center gap-1.5 text-xs font-medium text-red-600"
            >
              <i className="bi bi-exclamation-circle-fill" aria-hidden="true" />
              {termsError}
            </div>
          )}
        </div>

        <LegalModal
          content={termsContent}
          open={openLegalDoc === "terms"}
          onClose={() => setOpenLegalDoc(null)}
          showVersion
        />
        <LegalModal
          content={privacyContent}
          open={openLegalDoc === "privacy"}
          onClose={() => setOpenLegalDoc(null)}
        />

        <div className="form-item mt-7">
          <button
            type="submit"
            className={primaryButtonClass}
            disabled={submitting}
          >
            {submitting ? (
              t("Signup.form.submitting")
            ) : (
              <>
                {t("Signup.form.submit")}
                <i className="bi bi-arrow-right ml-2" aria-hidden="true" />
              </>
            )}
          </button>
        </div>
      </form>

      <Toast
        open={open}
        message={errorMessage}
        onClose={handleClose}
        severity="error"
      />
    </>
  );
};

SignupForm.propTypes = {
  role: PropTypes.number.isRequired,
  onRoleChange: PropTypes.func,
  inviteToken: PropTypes.string,
  onRegistered: PropTypes.func.isRequired,
};

/**
 * Post-signup confirmation. The account exists but cannot sign in until the
 * emailed link is followed, so this screen's job is to set that expectation
 * before the user tries to log in and fails.
 */
const CheckYourEmail = ({ email }) => {
  const { t } = useTranslation();

  return (
    <div className="signup-sent text-center">
      <span className="inline-flex size-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
        <i className="bx bx-envelope text-2xl" aria-hidden="true" />
      </span>

      <h1 className="mt-4 text-fluid-xl font-bold tracking-tight text-ink-900">
        {t("Signup.sent.title")}
      </h1>

      {/* Split around the address rather than interpolated, so it keeps its own
          <strong> without pulling <Trans> into the tests' i18n mock. Both
          languages put the email in the same position. */}
      <p className="mt-2 text-fluid-sm leading-relaxed text-ink-500">
        {t("Signup.sent.bodyBefore")}{" "}
        <strong className="break-all text-ink-700">{email}</strong>
        {t("Signup.sent.bodyAfter")}
      </p>

      <p className="mt-4 text-xs text-ink-400">
        {t("Signup.sent.validity")}{" "}
        <Link to="/activate" className={textLinkClass}>
          {t("Signup.sent.requestNew")}
        </Link>
        .
      </p>
    </div>
  );
};

CheckYourEmail.propTypes = {
  email: PropTypes.string.isRequired,
};

export default Signup;
